import React, { useState, useEffect } from 'react';
import { Card, Row, Col, DatePicker, Table, Statistic, Input, Tooltip, Tag } from 'antd';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import AV from 'leancloud-storage';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Search } = Input;

// Constants for base costs
const BASE_COSTS = {
  NON_TRANSFER: {
    LOCAL: 2300,
    LOCAL_VIP: 3500,
    INSIDE_PROVINCE: 5000,
    OUTSIDE_PROVINCE: 5500
  },
  TRANSFER: {
    LOCAL: 2300,
    OTHER: 4500
  }
};

const ReferrerBonus = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [searchText, setSearchText] = useState('');
  const [bonusData, setBonusData] = useState([]);
  const [summary, setSummary] = useState({
    totalStudents: 0,
    totalTuition: 0,
    totalBaseCost: 0,
    totalBonus: 0
  });

  // Calculate base cost for a student
  const calculateBaseCost = (student) => {
    const isTransferred = student.get('transferred');
    const isLocal = student.get('local');
    const isVIP = student.get('VIP');
    const address = student.get('address') || '';
    const isInsideProvince = address.includes('黑龙江省');

    if (isTransferred) {
      return isLocal ? BASE_COSTS.TRANSFER.LOCAL : BASE_COSTS.TRANSFER.OTHER;
    } else {
      if (isLocal) {
        return isVIP ? BASE_COSTS.NON_TRANSFER.LOCAL_VIP : BASE_COSTS.NON_TRANSFER.LOCAL;
      } else {
        return isInsideProvince ? BASE_COSTS.NON_TRANSFER.INSIDE_PROVINCE : BASE_COSTS.NON_TRANSFER.OUTSIDE_PROVINCE;
      }
    }
  };

  // Calculate bonus for a student
  const calculateBonus = (student) => {
    const tuitionFee = student.get('tuitionFee') || 0;
    const baseCost = calculateBaseCost(student);
    return Math.max(0, tuitionFee - baseCost);
  };

  const fetchBonusData = async () => {
    try {
      setLoading(true);
      const [startDate, endDate] = dateRange;

      // Query students who signed up within the date range
      const query = new AV.Query('Student');
      query.greaterThanOrEqualTo('signUpDate', startDate.format('YYYY-MM-DD'));
      query.lessThanOrEqualTo('signUpDate', endDate.format('YYYY-MM-DD'));
      query.descending('signUpDate');
      query.limit(1000); // Set a high limit to get all results

      const students = await query.find();

      // Process each student to calculate bonus
      const result = [];
      let totalTuition = 0;
      let totalBaseCost = 0;
      let totalBonus = 0;

      students.forEach(student => {
        const referrer = student.get('referrer');
        if (!referrer) return; // Skip students without referrers
        
        const tuitionFee = student.get('tuitionFee') || 0;
        const baseCost = calculateBaseCost(student);
        const bonus = calculateBonus(student);
        
        totalTuition += tuitionFee;
        totalBaseCost += baseCost;
        totalBonus += bonus;

        result.push({
          key: student.id,
          signUpDate: student.get('signUpDate'),
          name: student.get('name'),
          fileId: student.get('fileId'),
          referrer: referrer,
          transferred: student.get('transferred'),
          local: student.get('local'),
          vip: student.get('VIP'),
          address: student.get('address'),
          tuitionFee: tuitionFee,
          baseCost: baseCost,
          bonus: bonus
        });
      });

      // Group by referrer
      const referrerMap = result.reduce((acc, curr) => {
        const { referrer } = curr;
        if (!acc[referrer]) {
          acc[referrer] = {
            referrer,
            students: [],
            totalStudents: 0,
            totalTuition: 0,
            totalBonus: 0
          };
        }
        
        acc[referrer].students.push(curr);
        acc[referrer].totalStudents += 1;
        acc[referrer].totalTuition += curr.tuitionFee;
        acc[referrer].totalBonus += curr.bonus;
        
        return acc;
      }, {});

      const referrerData = Object.values(referrerMap)
        .sort((a, b) => b.totalBonus - a.totalBonus); // Sort by total bonus

      setBonusData(referrerData);
      setSummary({
        totalStudents: students.length,
        totalTuition,
        totalBaseCost,
        totalBonus
      });

    } catch (error) {
      console.error('Error fetching bonus data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonusData();
  }, [dateRange]);

  // Filter data based on search text
  const filteredData = bonusData.filter(item => 
    item.referrer.toLowerCase().includes(searchText.toLowerCase())
  );

  // Define expandable row for student details
  const expandedRowRender = (record) => {
    const columns = [
      { 
        title: '报名日期', 
        dataIndex: 'signUpDate', 
        key: 'signUpDate' 
      },
      { 
        title: '学员姓名', 
        dataIndex: 'name', 
        key: 'name' 
      },
      { 
        title: '档案号', 
        dataIndex: 'fileId', 
        key: 'fileId' 
      },
      {
        title: '学员类型',
        key: 'type',
        render: (_, record) => {
          let tags = [];
          if (record.transferred) tags.push(<Tag color="blue">转入</Tag>);
          if (record.local) tags.push(<Tag color="green">本地</Tag>);
          if (record.vip) tags.push(<Tag color="gold">VIP</Tag>);
          return tags;
        }
      },
      { 
        title: '学费', 
        dataIndex: 'tuitionFee', 
        key: 'tuitionFee',
        render: val => `¥${val.toFixed(2)}`
      },
      { 
        title: '本金', 
        dataIndex: 'baseCost', 
        key: 'baseCost',
        render: val => `¥${val.toFixed(2)}`
      },
      { 
        title: '提成', 
        dataIndex: 'bonus', 
        key: 'bonus',
        render: val => `¥${val.toFixed(2)}`
      }
    ];

    return (
      <Table
        columns={columns}
        dataSource={record.students}
        pagination={false}
        size="small"
      />
    );
  };

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={12}>
            <Search
              placeholder="搜索推荐人"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={value => setSearchText(value)}
              onChange={e => setSearchText(e.target.value)}
            />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={6}>
            <Statistic
              title="学员总数"
              value={summary.totalStudents}
              loading={loading}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="总学费"
              value={summary.totalTuition}
              precision={2}
              prefix="¥"
              loading={loading}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="总本金"
              value={summary.totalBaseCost}
              precision={2}
              prefix="¥"
              loading={loading}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title={
                <span>
                  总提成
                  <Tooltip title="提成 = 学费 - 本金">
                    <InfoCircleOutlined style={{ marginLeft: 4 }} />
                  </Tooltip>
                </span>
              }
              value={summary.totalBonus}
              precision={2}
              prefix="¥"
              loading={loading}
            />
          </Col>
        </Row>
      </Card>

      <Card title="推荐人提成统计">
        <Table
          loading={loading}
          dataSource={filteredData}
          rowKey="referrer"
          expandable={{
            expandedRowRender,
          }}
          columns={[
            {
              title: '推荐人',
              dataIndex: 'referrer',
              key: 'referrer',
            },
            {
              title: '推荐学员数',
              dataIndex: 'totalStudents',
              key: 'totalStudents',
              sorter: (a, b) => a.totalStudents - b.totalStudents,
            },
            {
              title: '总学费',
              dataIndex: 'totalTuition',
              key: 'totalTuition',
              render: val => `¥${val.toFixed(2)}`,
              sorter: (a, b) => a.totalTuition - b.totalTuition,
            },
            {
              title: '总提成',
              dataIndex: 'totalBonus',
              key: 'totalBonus',
              render: val => `¥${val.toFixed(2)}`,
              sorter: (a, b) => a.totalBonus - b.totalBonus,
              defaultSortOrder: 'descend',
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default ReferrerBonus; 